import { inject } from '@angular/core';
import { HttpHeaders, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { tap } from 'rxjs';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { config } from '../../../environments/environment';
import { SessionService } from '../services/session.service';
import { PRESERVE_RECOVERY_IDENTITY } from './recovery-context';
import { tenantHeaders } from '../../shared/helpers/tenant';

/**
 * Headers every backend request carries. Shared with the few requests that
 * bypass `HttpClient` (streamed downloads), which must send credentials the
 * same way (plus `credentials: 'include'`).
 */
export function backendRequestHeaders(
  sessionToken: string,
  userName: string | null | undefined
): Record<string, string> {
  const headers: Record<string, string> = { ...tenantHeaders(), 'X-User-Name': userName ?? '' };
  // The signed session token is the credential: the httpOnly cookie is
  // third-party in production and blocked by most browsers, so the explicit
  // `Authorization: Bearer` replay is what keeps identity stable across
  // reloads (and is the only carrier in Electron / native clients).
  if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
  return headers;
}

export const defaultHeadersInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.url.startsWith('./') || !config.backendUrl) return next(req);
  const userStore = inject(UserSettingsStore);
  const session = inject(SessionService);
  const url = new URL(req.url);
  const backendUrl = new URL(config.backendUrl);
  if (backendUrl.host === url.host) {
    const headers = new HttpHeaders(
      backendRequestHeaders(session.sessionToken(), userStore.value.userName)
    );
    const modifiedReq = req.clone({
      headers,
      withCredentials: true,
    });
    // The server echoes the (possibly re-minted) session token on every
    // response — adopt it so an expired/rotated token heals on the next
    // round trip instead of wedging the session.
    return next(modifiedReq).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          const token = event.headers.get('x-andor-uuid');
          if (token && !req.context.get(PRESERVE_RECOVERY_IDENTITY))
            session.updateFromHeader(token);
        }
      })
    );
  }
  return next(req);
};
